import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Instagram } from "lucide-react";

export const houseSaundersImages = [
  // Lounge
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/q6czf4k2_IMG_2492.webp",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/3lpqkcy7_IMG_2499.webp",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/d7l7bviq_IMG_2552.webp",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/v2pf36ft_ChatGPT%20Image%20Sep%2030%2C%202026%2C%2003_20_10%20PM.png",
  // Kitchen
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/4o74fjm8_IMG_2502%20-%20Copy.webp",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/1z52wpjq_IMG_2507%20-%20Copy.webp",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/ny4wncvc_IMG_2509%20-%20Copy.webp",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/cvossjg0_IMG_2517%20-%20Copy.webp",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/rshgjx1w_IMG_2525.webp",
  // Outdoor — sea-view window
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/8k5nrlxr_Image%20%2832%29.webp",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/pqrm00qu_ChatGPT%20Image%20Sep%2030%2C%202026%2C%2003_17_52%20PM.png",
  // Bedrooms (Group 3 kept together)
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/u8a553kh_Image%20%2818%29.webp",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/acotlh5i_IMG_2653.webp",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/1q19vv2e_ChatGPT%20Image%20Sep%2030%2C%202026%2C%2004_22_59%20PM.png",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/oz3mrec4_ChatGPT%20Image%20Sep%2030%2C%202026%2C%2004_41_17%20PM.png",
  // Bathrooms (Group 1 & Group 2 kept together)
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/yx5il8ql_ChatGPT%20Image%20Sep%2030%2C%202026%2C%2004_29_21%20PM.png",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/j4xxcqm7_ChatGPT%20Image%20Sep%2030%2C%202026%2C%2004_29_21%20PM%20-%20Copy.png",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/ytmosad3_ChatGPT%20Image%20Sep%2030%2C%202026%2C%2004_10_28%20PM.png",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/jjd6dv5u_ChatGPT%20Image%20Sep%2030%2C%202026%2C%2004_12_43%20PM.png",
];

const INSTAGRAM_REEL = "https://www.instagram.com/reel/DcOeTuhs5wN/";

export default function HouseSaundersPage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const thumbRefs = useRef([]);

  // Preload images
  useEffect(() => {
    houseSaundersImages.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Auto-advance slideshow — slow, elegant
  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % houseSaundersImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isPaused]);

  // Keep active thumbnail in view
  useEffect(() => {
    const el = thumbRefs.current[currentSlide];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [currentSlide]);

  const pauseBriefly = useCallback(() => {
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 8000);
  }, []);

  const goNext = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % houseSaundersImages.length);
    pauseBriefly();
  }, [pauseBriefly]);

  const goPrev = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + houseSaundersImages.length) % houseSaundersImages.length);
    pauseBriefly();
  }, [pauseBriefly]);

  const goTo = useCallback((idx) => {
    setCurrentSlide(idx);
    pauseBriefly();
  }, [pauseBriefly]);

  return (
    <div data-testid="house-saunders-page" className="bg-cream">

      {/* Editorial Header */}
      <section className="pt-28 md:pt-36 pb-8 md:pb-12" data-testid="hs-header">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 text-black/40 hover:text-warmBrown transition-colors duration-300 font-body text-xs uppercase tracking-[0.2em] mb-10"
            data-testid="hs-back-link"
          >
            <ArrowLeft size={14} />
            Back to Projects
          </Link>

          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-black leading-[1.05] mb-4" data-testid="hs-title">
            333B &ndash; House <span className="italic text-warmBrown">Saunders</span>
          </h1>

          <p className="text-warmBrown uppercase tracking-[0.3em] text-xs font-body mb-8">
            Bantry Bay, Cape Town
          </p>

          <p className="text-black/60 font-body text-sm md:text-base leading-[1.85] max-w-2xl mx-auto">
            House Saunders is a refined expression of timeless luxury, where carefully curated layers, rich materials and thoughtful detailing come together to create a home that feels both sophisticated and effortlessly comfortable.
          </p>
        </div>
      </section>

      {/* Full-width Centered Slideshow */}
      <section className="pb-16 md:pb-24" data-testid="hs-slideshow">
        <div
          className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Main Viewer */}
          <div className="relative" data-testid="hs-main-viewer">
            <div className="relative aspect-[16/10] md:aspect-[16/9] overflow-hidden">
              {houseSaundersImages.map((src, idx) => (
                <img
                  key={src}
                  src={src}
                  alt={`House Saunders ${idx + 1}`}
                  className="absolute inset-0 w-full h-full object-contain transition-opacity duration-[1200ms] ease-in-out"
                  style={{ opacity: idx === currentSlide ? 1 : 0 }}
                />
              ))}
            </div>

            {/* Side Arrows */}
            <button
              onClick={goPrev}
              className="absolute left-2 md:-left-4 top-1/2 -translate-y-1/2 text-black/50 hover:text-warmBrown bg-cream/70 hover:bg-cream p-2 md:p-3 transition-all duration-300 backdrop-blur-sm"
              data-testid="hs-prev"
              aria-label="Previous image"
            >
              <ChevronLeft size={26} strokeWidth={1.25} />
            </button>
            <button
              onClick={goNext}
              className="absolute right-2 md:-right-4 top-1/2 -translate-y-1/2 text-black/50 hover:text-warmBrown bg-cream/70 hover:bg-cream p-2 md:p-3 transition-all duration-300 backdrop-blur-sm"
              data-testid="hs-next"
              aria-label="Next image"
            >
              <ChevronRight size={26} strokeWidth={1.25} />
            </button>

            {/* Counter */}
            <div className="absolute bottom-3 right-3 bg-black/30 backdrop-blur-sm text-white/90 text-xs font-body tracking-[0.15em] px-3 py-1">
              {String(currentSlide + 1).padStart(2, "0")} / {String(houseSaundersImages.length).padStart(2, "0")}
            </div>
          </div>

          {/* Thumbnail Strip */}
          <div
            className="mt-4 md:mt-6 flex gap-2 md:gap-3 overflow-x-auto pb-2 hs-thumb-strip"
            data-testid="hs-thumb-strip"
          >
            {houseSaundersImages.map((src, idx) => (
              <button
                key={src}
                ref={(el) => (thumbRefs.current[idx] = el)}
                onClick={() => goTo(idx)}
                className={`relative flex-shrink-0 w-20 h-16 md:w-28 md:h-20 overflow-hidden transition-all duration-300 ${
                  idx === currentSlide
                    ? "ring-2 ring-warmBrown opacity-100"
                    : "opacity-50 hover:opacity-90"
                }`}
                data-testid={`hs-thumb-${idx}`}
                aria-label={`View image ${idx + 1}`}
              >
                <img src={src} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>

          {/* Full Description + Instagram */}
          <div className="max-w-2xl mx-auto text-center mt-14 md:mt-20 space-y-5 text-black/60 font-body text-sm md:text-base leading-[1.85]">
            <p>
              Inspired by its natural surroundings, the interior embraces soft, earthy tones, layered textures and an abundance of natural light. Each space has been intentionally considered to create a warm connection between contemporary luxury, everyday living and the beauty of nature.
            </p>
            <p>
              At the heart of the home, the kitchen balances elegance with functionality &mdash; a beautifully resolved space designed not only to be admired, but to be lived in.
            </p>
            <p className="italic text-warmBrown/80 pt-2">
              Curated layers. Natural warmth. Timeless design.
            </p>

            <a
              href={INSTAGRAM_REEL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 pt-4 text-black/40 hover:text-warmBrown transition-colors duration-300 font-body text-xs uppercase tracking-[0.15em]"
              data-testid="hs-instagram-reel"
            >
              <Instagram size={16} />
              Watch the Reel
            </a>
          </div>
        </div>
      </section>

      {/* Instagram Reel Embed */}
      <section className="py-16 md:py-24 bg-soft-beige" data-testid="hs-reel-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="text-warmBrown uppercase tracking-[0.3em] text-xs font-body mb-4">
            Behind the Design
          </p>
          <h2 className="font-heading text-3xl md:text-4xl text-black mb-8">
            Watch the <span className="italic text-warmBrown">Reel</span>
          </h2>

          <div className="mx-auto max-w-md">
            <div className="relative" style={{ paddingBottom: '177%', height: 0, overflow: 'hidden' }}>
              <iframe
                src="https://www.instagram.com/reel/DcOeTuhs5wN/embed/"
                className="absolute top-0 left-0 w-full h-full border-0"
                allowFullScreen
                scrolling="no"
                title="House Saunders Instagram Reel"
                loading="lazy"
              />
            </div>
          </div>

          <a
            href={INSTAGRAM_REEL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 mt-8 text-warmBrown hover:text-black transition-colors duration-300 font-body text-sm uppercase tracking-[0.15em]"
            data-testid="hs-reel-link"
          >
            <Instagram size={16} />
            View on Instagram
          </a>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-warmBrown text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
            Ready to Create Your Space?
          </h2>
          <p className="text-white/70 font-body text-sm md:text-base mb-8 max-w-lg mx-auto">
            Every great home starts with a conversation. Tell us about your vision.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-white text-black hover:bg-champagne transition-all duration-500 uppercase tracking-[0.2em] text-xs px-8 py-4 font-semibold"
            data-testid="hs-cta"
          >
            Start Your Project
          </Link>
        </div>
      </section>
    </div>
  );
}
